/*
 * GTender desktop resizable panels.
 * Presentation only: reuses the existing results/preview DOM and does not own search,
 * tender details, authentication, storage or network requests.
 */
(function () {
  'use strict';

  var VERSION = '2026-10-26-gtender-resize-01';
  var ROOT = '[data-gtender-public-entry]';
  var DESKTOP = '(min-width: 1024px)';
  var GAP = 16;
  var DEFAULT_RATIO = 0.4;
  var KEY_STEP = 16;

  function setting(node, name, fallback) {
    var raw = Number(node.getAttribute(name));
    return Number.isFinite(raw) && raw > 0 ? raw : fallback;
  }

  function clamp(number, min, max) {
    return Math.min(max, Math.max(min, number));
  }

  function mount(root) {
    var workbench = root.querySelector('[data-gtender-public-workbench]');
    var left = root.querySelector('[data-gtender-public-list-column]');
    var right = root.querySelector('[data-gtender-public-preview-column]');
    if (
      !workbench ||
      !left ||
      !right ||
      workbench.getAttribute('data-gtender-resize-bound') === VERSION
    )
      return;
    workbench.setAttribute('data-gtender-resize-bound', VERSION);

    var media = window.matchMedia(DESKTOP);
    var handle = document.createElement('div');
    var locale = root.getAttribute('data-locale') || 'en';
    var zh = /^zh/i.test(locale);
    var hans = /^zh-CN/i.test(locale);
    var label = hans
      ? '调整招标列表和详情面板宽度'
      : zh
        ? '調整招標列表與詳情面板寬度'
        : 'Resize tender results and details panels';

    if (!left.id) left.id = root.id + '-list-panel';
    if (!right.id) right.id = root.id + '-preview-panel';

    handle.className = 'gtender-resize-handle';
    handle.setAttribute('data-gtender-resize-handle', '');
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-orientation', 'vertical');
    handle.setAttribute('aria-label', label);
    handle.setAttribute('aria-controls', left.id + ' ' + right.id);
    handle.setAttribute('tabindex', '0');
    handle.title = zh ? '拖曳調整寬度，雙擊重設' : 'Drag to resize; double-click to reset';
    handle.hidden = true;
    workbench.appendChild(handle);

    var width = null;
    var manuallyAdjusted = false;
    var pointerId = null;
    var startX = 0;
    var startWidth = 0;
    var queuedWidth = null;
    var frame = 0;
    var syncFrame = 0;
    var observer = null;

    function bounds() {
      var total = workbench.getBoundingClientRect().width;
      var available = Math.max(0, total - GAP);
      var minLeft = setting(workbench, 'data-gtender-resize-left-min', 300);
      var maxLeft = setting(workbench, 'data-gtender-resize-left-max', 760);
      var minRight = setting(workbench, 'data-gtender-resize-right-min', 360);
      var maxRight = setting(workbench, 'data-gtender-resize-right-max', 2400);
      var min = Math.max(minLeft, available - maxRight);
      var max = Math.min(maxLeft, available - minRight);
      return max >= min && total > 0 ? { available: available, min: min, max: max } : null;
    }

    function apply(next) {
      var range = bounds();
      if (!media.matches || !range) return;
      width = clamp(next, range.min, range.max);
      var rounded = Math.round(width);
      workbench.style.setProperty('--gtender-resize-left', rounded + 'px');
      workbench.setAttribute('data-gtender-resizable', 'true');
      handle.hidden = false;
      handle.setAttribute('aria-valuemin', String(Math.ceil(range.min)));
      handle.setAttribute('aria-valuemax', String(Math.floor(range.max)));
      handle.setAttribute('aria-valuenow', String(rounded));
      handle.setAttribute(
        'aria-valuetext',
        zh
          ? '列表 ' + rounded + ' 像素；詳情 ' + Math.round(range.available - width) + ' 像素'
          : 'Results ' +
              rounded +
              ' pixels; details ' +
              Math.round(range.available - width) +
              ' pixels'
      );
    }

    function sync() {
      if (!media.matches || !bounds()) {
        if (pointerId !== null) stop();
        workbench.removeAttribute('data-gtender-resizable');
        handle.hidden = true;
        return;
      }
      var range = bounds();
      var desired = manuallyAdjusted && width !== null ? width : range.available * DEFAULT_RATIO;
      apply(desired);
    }

    function requestSync() {
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(function () {
        syncFrame = 0;
        sync();
      });
    }

    function flush() {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
      if (queuedWidth !== null) {
        apply(queuedWidth);
        queuedWidth = null;
      }
    }

    function queue(next) {
      queuedWidth = next;
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        if (queuedWidth === null) return;
        var target = queuedWidth;
        queuedWidth = null;
        apply(target);
      });
    }

    function stop() {
      if (pointerId === null) return;
      flush();
      var previous = pointerId;
      pointerId = null;
      workbench.removeAttribute('data-gtender-resizing');
      document.documentElement.removeAttribute('data-gtender-panel-dragging');
      if (handle.hasPointerCapture && handle.hasPointerCapture(previous)) {
        handle.releasePointerCapture(previous);
      }
    }

    function onPointerDown(event) {
      if (
        !media.matches ||
        !bounds() ||
        pointerId !== null ||
        (event.pointerType === 'mouse' && event.button !== 0)
      )
        return;
      event.preventDefault();
      pointerId = event.pointerId;
      startX = event.clientX;
      startWidth = width === null ? bounds().available * DEFAULT_RATIO : width;
      manuallyAdjusted = true;
      workbench.setAttribute('data-gtender-resizing', 'true');
      document.documentElement.setAttribute('data-gtender-panel-dragging', 'true');
      handle.setPointerCapture(pointerId);
    }

    function onPointerMove(event) {
      if (event.pointerId !== pointerId) return;
      queue(startWidth + event.clientX - startX);
    }

    function onPointerEnd(event) {
      if (event.pointerId === pointerId) stop();
    }

    function onKeyDown(event) {
      if (!media.matches || !bounds()) return;
      var range = bounds();
      var current = width === null ? range.available * DEFAULT_RATIO : width;
      var next = current;
      var step = event.shiftKey ? KEY_STEP * 3 : KEY_STEP;
      switch (event.key) {
        case 'ArrowLeft':
          next -= step;
          break;
        case 'ArrowRight':
          next += step;
          break;
        case 'Home':
          next = range.min;
          break;
        case 'End':
          next = range.max;
          break;
        default:
          return;
      }
      event.preventDefault();
      manuallyAdjusted = true;
      apply(next);
    }

    function onReset() {
      manuallyAdjusted = false;
      sync();
    }

    function onLostCapture(event) {
      if (event.pointerId === pointerId) stop();
    }

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerEnd);
    handle.addEventListener('pointercancel', onPointerEnd);
    handle.addEventListener('lostpointercapture', onLostCapture);
    handle.addEventListener('keydown', onKeyDown);
    handle.addEventListener('dblclick', onReset);
    window.addEventListener('blur', stop);
    window.addEventListener('resize', requestSync);

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(requestSync);
      observer.observe(workbench);
    }
    if (media.addEventListener) media.addEventListener('change', requestSync);
    else if (media.addListener) media.addListener(requestSync);

    function onSectionUnload(event) {
      if (event.target !== root && !event.target.contains(root)) return;
      stop();
      if (observer) observer.disconnect();
      if (syncFrame) window.cancelAnimationFrame(syncFrame);
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('blur', stop);
      window.removeEventListener('resize', requestSync);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      if (media.removeEventListener) media.removeEventListener('change', requestSync);
      else if (media.removeListener) media.removeListener(requestSync);
    }
    document.addEventListener('shopify:section:unload', onSectionUnload);
    sync();
  }

  function boot(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    if (host.matches && host.matches(ROOT)) mount(host);
    Array.prototype.forEach.call(host.querySelectorAll(ROOT), mount);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target instanceof Element ? event.target : document);
  });
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})();
